import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, MenuItem, Popover, Stack, TextField } from '@mui/material';
import _ from 'lodash';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FilterPanelApplyButton, FilterPanelClearAllButton } from './grid.styles';
import { operatorsByType } from './utility/filterFns';
import type { IColumnFilterValue } from './utility/filterFns';
import type { TPhoenixTable } from './utility/types';

interface IFilterRow {
  rowKey: number;
  field: string;
  operator: string;
  value: string;
}

type TFilterLogic = 'and' | 'or';

interface IGridFilterPanelProps {
  table: TPhoenixTable;
  anchorEl: HTMLElement | null;
  tableContainerRef: any;
  initialField?: string;
  filterLogic: string; onFilterLogicChange: (value: 'and' | 'or') => void;
  onClose: () => void;
}

let rowKeySeq = 0;
const nextRowKey = () => ++rowKeySeq;

export const GridFilterPanel = ({ table, anchorEl, tableContainerRef, initialField, filterLogic, onFilterLogicChange, onClose }: IGridFilterPanelProps) => {
  const filterableColumns = table.getAllLeafColumns().filter((column) => column.getCanFilter());
  const [tableRect, setTableRect] = useState<DOMRect | null>(null);

  useLayoutEffect(() => {
    const updateTableRect = () => {
      if (tableContainerRef.current) {
        setTableRect(tableContainerRef.current.getBoundingClientRect());
      }
    };
    updateTableRect();
    window.addEventListener('resize', updateTableRect);
    window.addEventListener('scroll', updateTableRect, true);
    const resizeObserver = new ResizeObserver(updateTableRect);

    if (tableContainerRef.current) {
      resizeObserver.observe(tableContainerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateTableRect);
      window.removeEventListener('scroll', updateTableRect, true);
      resizeObserver.disconnect();
    };
  }, []);

  const buildRowsFromState = (): IFilterRow[] => {
    const active = table.state.columnFilters.map((filter) => {
      const filterValue = filter.value as IColumnFilterValue;

      const rawValue = filterValue?.value;

      return {
        rowKey: nextRowKey(),
        field: filter.id,
        operator: filterValue?.operator ?? 'contains',
        value:
          typeof rawValue === 'string' || typeof rawValue === 'number'
            ? String(rawValue)
            : '',
      };
    });

    if (initialField && !active.some((row) => row.field === initialField)) {
      const type = table.getColumn(initialField)?.columnDef.meta?.type ?? 'string';

      active.push({
        rowKey: nextRowKey(),
        field: initialField,
        operator: operatorsByType[type]?.[0]?.value ?? 'contains',
        value: '',
      });
    }

    return active;
  };

  const [rows, setRows] = useState<IFilterRow[]>(() =>
    buildRowsFromState()
  );
  const inputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const pendingFocusRowId = useRef<number | null>(null);

  useEffect(() => {
    const id = pendingFocusRowId.current;

    if (id !== null) {
      inputRefs.current[id]?.focus();
      pendingFocusRowId.current = null;
    }
  }, [rows]);

  useEffect(() => {
    if (!anchorEl) return;

    const nextFilters = rows
      .filter(
        (row) =>
          row.value !== '' &&
          row.value !== undefined &&
          row.value !== null
      )
      .map((row) => ({
        id: row.field,
        value: {
          operator: row.operator,
          value: row.value,
        } as IColumnFilterValue,
      }));

    // unconditionally re-renders the grid on every run and loops (Maximum update depth exceeded)
    if (!_.isEqual(nextFilters, table.state.columnFilters)) {
      table.setColumnFilters(nextFilters);
    }
  }, [rows, table, anchorEl]);

  const updateRow = (rowKey: number, patch: Partial<IFilterRow>) => {
    setRows((prev) => prev.map((row) => (row.rowKey === rowKey ? { ...row, ...patch } : row)));
  };

  const handleFieldChange = (rowKey: number, field: string) => {
    const type = table.getColumn(field)?.columnDef.meta?.type ?? 'string';
    updateRow(rowKey, { field, operator: operatorsByType[type]?.[0]?.value ?? 'contains', value: '' });
  };

  const removeRow = (rowKey: number) => {
    if (rows.length <= 1) {
      pendingFocusRowId.current = null;
      handleClearAll();
      return;
    }

    const removedIndex = rows.findIndex(
      (row) => row.rowKey === rowKey
    );

    if (removedIndex === -1) return;

    // Previous row, otherwise next row
    const targetRow = rows[removedIndex - 1] ?? rows[removedIndex + 1];

    setRows((prev) => {
      const filteredRows = prev.filter(
        (row) => row.rowKey !== rowKey
      );

      return filteredRows.map((row, index) => ({
        ...row,
        rowKey: index + 1,
      }));
    });

    // Calculate the new key after renumbering
    pendingFocusRowId.current = targetRow
      ? removedIndex === 0
        ? 1
        : removedIndex
      : null;
  };
  const addRow = () => {
    const usedFields = new Set(rows.map((row) => row.field));

    const nextColumn =
      filterableColumns.find(
        (column) => !usedFields.has(column.id)
      ) ?? filterableColumns[0];

    if (!nextColumn) return;

    const type = nextColumn.columnDef.meta?.type ?? 'string';

    const newRowKey = nextRowKey();

    pendingFocusRowId.current = newRowKey;

    setRows((prev) => [
      ...prev,
      {
        rowKey: newRowKey,
        field: nextColumn.id,
        operator: operatorsByType[type]?.[0]?.value ?? 'contains',
        value: '',
      },
    ]);
  };

  const handleApply = () => {
    const nextFilters = rows
      .filter((row) => row.value.trim() !== '')
      .map((row) => ({
        id: row.field,
        value: {
          operator: row.operator,
          value: row.value,
        } as IColumnFilterValue,
      }));

    table.setColumnFilters(nextFilters);
    onClose();
  };
  const handleClearAll = () => {
    table.resetColumnFilters();
    setRows([]);
    onFilterLogicChange('and');
    onClose();
  };
  const handlePopoverEntered = () => {
    const targetRow = rows.find((row) => row.field === initialField) ?? rows[0];
    const firstRowKey = targetRow?.rowKey;

    if (firstRowKey !== undefined) {
      inputRefs.current[firstRowKey]?.focus();
    }
  };
  useLayoutEffect(() => {
    if (!open) return;

    const rowKey = pendingFocusRowId.current;

    if (rowKey === null) return;

    const inputElement = inputRefs.current[rowKey];

    if (inputElement) {
      inputElement.focus();
      pendingFocusRowId.current = null;
    }
  }, [rows, open]);
  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={{
        top: (tableRect?.top ?? 0) + 40,
        left: tableRect?.left ?? 0,
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      TransitionProps={{
        onEntered: handlePopoverEntered,
      }}
    >
      <Stack
        spacing={2}
        sx={{ p: 1.5 }}
      >
        {rows.length === 0 && <Stack sx={{ color: 'text.secondary', fontSize: 14 }}>No filters applied.</Stack>}

        {rows.map((row, index) => {
          const type = table.getColumn(row.field)?.columnDef.meta?.type ?? 'string';
          const operators = operatorsByType[type] ?? operatorsByType.string;

          return (
            <Stack
              key={row.rowKey}
              direction="row"
              spacing={2}
              alignItems="flex-end"
              sx={{ paddingY: 1 }}
            >
              {rows.length > 1 ? (
                index === 0 ? (
                  <Box sx={{ minWidth: 70 }} />
                ) : (
                  <TextField
                    select
                    size="small"
                    value={filterLogic}
                    onChange={(event) =>
                      onFilterLogicChange(event.target.value as TFilterLogic)
                    }
                    sx={{
                      minWidth: 70,
                      '& .MuiSelect-select': {
                        fontWeight: '400 !important',
                      },
                      '& .MuiInputLabel-root': {
                        color: '#000',
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#000',
                      },
                      '& .MuiInputBase-input': {
                        fontWeight: '400 !important',
                      },
                    }}
                  >
                    <MenuItem value="and">And</MenuItem>
                    <MenuItem value="or">Or</MenuItem>
                  </TextField>
                )
              ) : null}
              <TextField
                select
                size="small"
                label="Columns"
                sx={{
                  minWidth: 100,
                  '& .MuiSelect-select': {
                    fontWeight: '400 !important',
                  },
                  '& .MuiInputLabel-root': {
                    color: '#000',

                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#000',
                  },
                  '& .MuiInputBase-input': {
                    fontWeight: '400 !important',
                  },
                }}
                value={row.field}
                onChange={(event) => handleFieldChange(row.rowKey, event.target.value)}
              >
                {filterableColumns.map((column) => (
                  <MenuItem
                    key={column.id}
                    value={column.id}
                  >
                    {column.columnDef.meta?.headerName ?? column.id}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Operator"
                sx={{
                  minWidth: 100,
                  '& .MuiInputLabel-root': {
                    color: '#000',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#000',
                  },
                  '& .MuiInputBase-input': {
                    fontWeight: 400,
                  },
                }}
                value={row.operator}
                onChange={(event) => updateRow(row.rowKey, { operator: event.target.value })}
              >
                {operators.map((op) => (
                  <MenuItem
                    key={op.value}
                    value={op.value}
                    style={{ fontWeight: 400 }}
                  >
                    {op.label}
                  </MenuItem>
                ))}
              </TextField>

              {type === 'boolean' ? (
                <TextField
                  select
                  autoFocus
                  focused
                  color="primary"
                  size="small"
                  label="Value"
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: '#000',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#000',
                    },
                    '& .MuiInputBase-input': {
                      fontWeight: 400,
                    },
                  }}
                  value={row.value ?? ''}
                  onChange={(event) => updateRow(row.rowKey, { value: event.target.value })}
                >
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
              ) : (
                <TextField
                  size="small"
                  label="Value"
                  placeholder="Filter value"
                  sx={{
                    minWidth: 125,
                    '& .MuiInputLabel-root': {
                      color: '#000',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#000',
                    },
                    '& .MuiInputBase-input': {
                      fontWeight: 400,
                      fontSize: '12px',
                    },

                    '& .MuiInputBase-input::placeholder': {
                      fontSize: '12px',
                      fontWeight: 400,
                      opacity: 0.6,
                    },
                  }}
                  type={type === 'number' ? 'number' : type === 'date' || type === 'dateTime' ? 'date' : 'text'}
                  InputLabelProps={{ shrink: true }}
                  value={row.value}
                  onChange={(event) => {
                    const inputValue = event.target.value;

                    setRows((prevRows) =>
                      prevRows.map((item) =>
                        item.rowKey === row.rowKey
                          ? { ...item, value: inputValue }
                          : item
                      )
                    );
                  }}
                  inputRef={(el) => {
                    inputRefs.current[row.rowKey] = el;
                  }}
                />
              )}
              <IconButton
                size="small"
                onClick={() => removeRow(row.rowKey)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          );
        })}

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{
              cursor: 'pointer',
              padding: '6px 8px',
              color: 'primary.main',
              fontWeight: 'bold',

              '&:hover': {
                backgroundColor: 'rgba(54, 132, 176, 0.1)',
                cursor: 'pointer',
                borderRadius: 1,
              },
            }}
            onClick={addRow}
          >
            <AddIcon fontSize="small" />
            <span style={{ fontSize: 11 }}>Add filter</span>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
          >
            <FilterPanelClearAllButton
              variant="contained"
              onClick={handleClearAll}
            >
              ClearAll
            </FilterPanelClearAllButton>
            <FilterPanelApplyButton
              variant="contained"
              onClick={handleApply}
            >
              Apply
            </FilterPanelApplyButton>
          </Stack>
        </Stack>
      </Stack>
    </Popover>
  );
};
